// client/src/pages/CampaignManagement.js

import React, { useEffect, useRef, useState } from "react";
import "./CampaignManagement.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import Loading from "../components/Loading";

import cphereHeaderLogo from "../assets/Cphere Logo New.png";
import platformAnalyticsImage from "../assets/tiles/cpheresocial.tile.jpg";
import insightsImage from "../assets/tiles/manage.tile.jpg";
import analysisImage from "../assets/tiles/aioptimizations.tile.jpg";
import optimusImage from "../assets/tiles/optimize.tile.jpg";
import omniImage from "../assets/tiles/cphereprogrammatic.tile.jpg";
import accountImage from "../assets/tiles/settings.tile.jpg";

import { useNavigate } from "react-router-dom";

// Deterministic Platform Manager tile registry. ORDER IS A CONTRACT:
// Platform Analytics is always first and Omni Analytics always second
// (test-pinned); the remaining client-authorized tiles keep their existing
// relative order after those two. Insert new tiles AFTER index 1.
export const CLIENT_PLATFORM_MANAGER_TILES = Object.freeze([
  {
    key: "platform-analytics",
    label: "Platform",
    ariaLabel: "Platform Analytics",
    badge: "Analytics",
    image: platformAnalyticsImage,
    path: "/platform-analytics",
  },
  {
    key: "omni-analytics",
    label: "Omni",
    ariaLabel: "Omni Analytics",
    badge: "Analytics",
    image: omniImage,
    path: "/programmatic-analytics",
  },
  {
    key: "campaign-insights",
    label: "Campaign",
    ariaLabel: "Campaign Insights",
    badge: "Insights",
    image: insightsImage,
    path: "/ai-optimizations",
  },
  {
    key: "platform-intelligence",
    label: "Platform",
    ariaLabel: "Platform Intelligence",
    badge: "Intelligence",
    image: analysisImage,
    path: "/sherloc-reports",
  },
  {
    key: "platform-optimization",
    label: "Platform",
    ariaLabel: "Platform Optimization",
    badge: "Optimization",
    image: optimusImage,
    path: "/optimus",
  },
  {
    key: "account",
    label: "Account",
    ariaLabel: "Account",
    badge: null,
    image: accountImage,
    path: "/profile-hub",
  },
]);

/* ✅ module-level cache so returning to this page is instant */
let CLIENT_CAMPAIGN_MGMT_ASSETS_WARM = false;

function preloadImage(url, timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!url) return resolve();

    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      // decode helps reduce “flash” on first paint when supported
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };

    img.src = url;
  });
}

function waitForFonts(timeoutMs = 900) {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, timeoutMs);
    try {
      if (document?.fonts?.ready) {
        document.fonts.ready
          .then(() => {
            clearTimeout(t);
            resolve();
          })
          .catch(() => {
            clearTimeout(t);
            resolve();
          });
      } else {
        resolve();
      }
    } catch {
      clearTimeout(t);
      resolve();
    }
  });
}

const CampaignManagement = () => {
  const navigate = useNavigate();

  // ✅ GenesisHub-style loader state
  const [assetsReady, setAssetsReady] = useState(CLIENT_CAMPAIGN_MGMT_ASSETS_WARM);
  const [fadeIn, setFadeIn] = useState(false);
  const [loaderHidden, setLoaderHidden] = useState(false);
  const hideLoaderFallbackRef = useRef(null);

  // -----------------------------------------
  // ✅ Warm up key assets (PoweredBy + tiles + fonts)
  // -----------------------------------------
  useEffect(() => {
    let cancelled = false;

    if (CLIENT_CAMPAIGN_MGMT_ASSETS_WARM) {
      setAssetsReady(true);
      return;
    }

    (async () => {
      try {
        await Promise.all([
          preloadImage(cphereHeaderLogo),
          ...CLIENT_PLATFORM_MANAGER_TILES.map((tile) => preloadImage(tile.image)),
          waitForFonts(900),
        ]);
      } finally {
        if (cancelled) return;
        CLIENT_CAMPAIGN_MGMT_ASSETS_WARM = true;
        setAssetsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------------------
  // ✅ Fade in once ready (GenesisHub pattern)
  // -----------------------------------------
  useEffect(() => {
    if (!assetsReady) {
      setFadeIn(false);
      setLoaderHidden(false);
      if (hideLoaderFallbackRef.current) {
        clearTimeout(hideLoaderFallbackRef.current);
        hideLoaderFallbackRef.current = null;
      }
      return;
    }

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFadeIn(true));
    });

    hideLoaderFallbackRef.current = setTimeout(() => {
      setLoaderHidden(true);
      hideLoaderFallbackRef.current = null;
    }, 900);

    return () => cancelAnimationFrame(raf);
  }, [assetsReady]);

  const showLoader = !assetsReady || !loaderHidden;

  const handleReadyShellTransitionEnd = (e) => {
    if (e?.propertyName !== "opacity") return;
    if (!fadeIn) return;

    setLoaderHidden(true);

    if (hideLoaderFallbackRef.current) {
      clearTimeout(hideLoaderFallbackRef.current);
      hideLoaderFallbackRef.current = null;
    }
  };

  return (
    <div className="campaignmanagement-page-container">
      {/* ✅ Loader shell stays visible until ready shell fade-in completes */}
      {showLoader && (
        <div className="campaignmanagement-loading-shell">
          <Loading />
        </div>
      )}

      {/* ✅ Ready shell */}
      <div
        className={`campaignmanagement-ready-shell ${fadeIn ? "campaignmanagement-fade-in" : ""}`}
        aria-hidden={!assetsReady}
        onTransitionEnd={handleReadyShellTransitionEnd}
      >
        <div className="campaignmanagement-page-content">
          <CphereHeaderLogo
            alt="Powered by"
            className="campaignmanagement-poweredby-logo"
            onClick={() => navigate("/dashboard")}
            style={{ cursor: "pointer" }}
          />

          <div className="campaignmanagement-hero-container">
            <div
              className="campaignmanagement-hero-pill"
              role="heading"
              aria-level="1"
              aria-label="Platform Manager"
            >
              <span className="campaignmanagement-hero-pill-label cphere-hero-pill-wordmark">Platform</span>
              <span className="campaignmanagement-hero-pill-tag cphere-hero-pill-tag">Manager</span>
            </div>
            <div className="campaignmanagement-hero-shimmer-bar" />
          </div>

          <div className="campaignmanagement-button-container">
            {CLIENT_PLATFORM_MANAGER_TILES.map((tile) => (
              <button
                key={tile.key}
                type="button"
                className="campaignmanagement-tile"
                onClick={() => navigate(tile.path)}
                aria-label={tile.ariaLabel}
              >
                <div
                  className="campaignmanagement-tile-face"
                  style={{ backgroundImage: `url(${tile.image})` }}
                >
                  {tile.badge ? (
                    <span className="campaignmanagement-beta-tag">{tile.badge}</span>
                  ) : null}
                  <span className="campaignmanagement-tile-label">{tile.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignManagement;
